import { useEffect } from "react";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import RHFInput from "components/form/rhf-input";
import RHFTextarea from "components/form/rhf-textarea";

function buildThemeValues(themeEdit) {
    return {
        name: themeEdit?.name || "",
        description: themeEdit?.description || "",
    };
}

// Lives inside the <Dialog> so every open remounts the form with fresh
// defaults from the current themeEdit prop (recipe P5). themeEdit can
// change identity mid-open (it is React state in use-theming-state.js,
// stable per target), so the reset effect keys on that prop to
// re-baseline the form.
function CreateEditThemeForm({
    onClose,
    onSubmit,
    themeEdit,
    validationSchema,
}) {
    const methods = useForm({
        mode: "onChange",
        defaultValues: buildThemeValues(themeEdit),
        // `validationSchema` is optional at this layer (the dialog tests
        // exercise the undefined case); yupResolver(undefined) throws the
        // moment RHF first invokes it, so fall back to RHF's built-in
        // validation instead.
        resolver: validationSchema ? yupResolver(validationSchema) : undefined,
    });
    const { reset } = methods;
    // Read both flags up front: formState is a subscription proxy, so a
    // conditional read (`isValid && (isDirty || ...)`) short-circuits
    // before `isDirty` while isValid is false. RHF then never subscribes
    // to dirty updates and isDirty stays frozen at false.
    const { isValid, isDirty } = methods.formState;

    useEffect(() => {
        reset(buildThemeValues(themeEdit), { keepErrors: true });
    }, [themeEdit, reset]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <DialogBody className="dialog__body">
                    <div className="mb-4 p-2 bg-bb font-body bg-white rounded-xl">
                        <RHFInput
                            className="font-body"
                            name="name"
                            type="text"
                            label="Theme Name"
                            required
                        />
                    </div>
                    <div className="p-2 bg-bb font-body bg-white rounded-xl">
                        <RHFTextarea
                            color="blue-gray"
                            name="description"
                            className="text-lg font-body text-primary-text"
                            label="Theme Description (Optional)"
                        />
                    </div>
                </DialogBody>
                <DialogFooter className="dialog__footer">
                    <Button
                        type="button"
                        className="btn btn--secondary"
                        variant="filled"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={!(isValid && (isDirty || !themeEdit?.id))}
                        className="btn btn--primary"
                        variant="filled"
                    >
                        Save
                    </Button>
                </DialogFooter>
            </form>
        </FormProvider>
    );
}

export default function CreateEditThemeDialog({
    open,
    onClose,
    onSubmit,
    themeEdit,
    validationSchema,
}) {
    return (
        <Dialog open={open} handler={onClose} className="dialog">
            <DialogHeader className="dialog__header">
                Create/Edit Theme
            </DialogHeader>
            <CreateEditThemeForm
                onClose={onClose}
                onSubmit={onSubmit}
                themeEdit={themeEdit}
                validationSchema={validationSchema}
            />
        </Dialog>
    );
}
